import type React from 'react';
import {useState} from 'react';
import {Box, Link, Stack, TextField, Tooltip, Typography} from '@mui/material';
import {InfoOutlined} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import type {Opportunity} from '../../../types/strategyTypes';
import type {PlanProps} from '../strategyProps';
import {PICK_COLORS} from '../strategyProps';
import {MatrixChart} from '../MatrixChart';

const KIND = {saving: 'saving', avoidance: 'cost avoidance', value_added: 'value added'} as const;

/** A money value shown as text; click (or Enter) to type a new amount, saved on blur or Enter, Esc cancels. */
const ValueEditor: React.FC<{value: number; label: string; money: (v: number) => string; onCommit: (v: number) => void}> = (
    {value, label, money, onCommit}) => {
    const [editing, setEditing] = useState(false);
    if (!editing) {
        return (
            <Link component="button" type="button" variant="caption" underline="hover" color="text.secondary"
                  aria-label={`${label}: ${money(value)}. Change the value`} onClick={() => setEditing(true)}>
                {money(value)}
            </Link>
        );
    }
    return (
        <TextField
            type="number" size="small" defaultValue={Math.round(value)} autoFocus
            slotProps={{htmlInput: {min: 0, step: 1000, 'aria-label': `${label} value`, style: {width: 120, padding: '2px 6px'}}}}
            onBlur={e => {
                const v = Number(e.target.value);
                setEditing(false);
                if (Number.isFinite(v) && v >= 0 && v !== value) onCommit(v);
            }}
            onKeyDown={e => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') setEditing(false);
            }}
        />
    );
};

/** PICK: opportunities by pay-off and difficulty. Quadrants split at 3: Implement, Challenge, Possible, Kill. */
export const PickTool: React.FC<PlanProps> = ({plan, actions, money}) => {
    const {content, derived, canEdit, reference} = plan;
    const spend = content.meta.influenceableSpend;
    const pctOf = (o: Opportunity) => o.pct ?? reference.opportunityPct[o.key] ?? 0;
    const standardOf = (o: Opportunity) => o.standardValue ?? Math.round(spend * pctOf(o)) / 100;
    const setValue = (o: Opportunity, value: number, manual: boolean) => void actions.save('pick',
        content.pick.map(x => (x.key === o.key ? {...x, value, manual, pct: pctOf(o), standardValue: standardOf(o)} : x)),
        manual ? `${o.name}: value set by hand.` : `${o.name}: back to the standard value.`);
    const explain = (o: Opportunity) => `Standard value: ${pctOf(o)}% of the category's spend (${money(spend)}) = `
        + `${money(standardOf(o))}. The same rule-of-thumb share is used for every category`
        + (o.key === 'supplier_consolidation' ? ' (3% with a long supplier tail, otherwise 1.5%)' : '')
        + '. Click the value to set your own; every change is kept in the change history.';
    const quadrant = new Map(derived.pick.map(p => [p.key, p.quadrant]));
    const ranked = [...content.pick].sort((a, b) => b.value - a.value || b.payoff - a.payoff);

    return (
        <Stack direction={{xs: 'column', lg: 'row'}} gap={3} alignItems={{xs: 'stretch', lg: 'flex-start'}}>
            <MatrixChart
                ariaLabel="PICK chart" xLabel="Difficulty" yLabel="Pay-off" min={1} max={5} split={3}
                quadrants={['Implement', 'Challenge', 'Possible', 'Kill']}
                points={content.pick.map(o => ({id: o.key, label: o.name, x: o.difficulty, y: o.payoff,
                    color: PICK_COLORS[quadrant.get(o.key) ?? 'Possible']}))}
                readOnly={!canEdit}
                onMove={(id, x, y) => void actions.save('pick',
                    content.pick.map(o => (o.key === id ? {...o, difficulty: x, payoff: y} : o)), 'Opportunity moved.')}
            />
            <Stack gap={1.25} sx={{flex: 1, minWidth: {lg: 280}}}>
                {ranked.map(o => (
                    // Dot on the first line, name and value stacked so long names wrap cleanly, tag kept whole.
                    <Stack key={o.key} direction="row" gap={1} alignItems="flex-start">
                        <Box sx={{width: 10, height: 10, mt: '5px', flexShrink: 0, borderRadius: '50%',
                            bgcolor: PICK_COLORS[quadrant.get(o.key) ?? 'Possible']}}/>
                        <Box sx={{flex: 1, minWidth: 0}}>
                            <Typography variant="body2">{o.name}</Typography>
                            {o.kind === 'value_added' ? (
                                <Typography variant="caption" color="text.secondary">{KIND[o.kind]}</Typography>
                            ) : (
                                <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                                    {canEdit
                                        ? <ValueEditor value={o.value} label={o.name} money={money} onCommit={v => setValue(o, v, true)}/>
                                        : <Typography variant="caption" color="text.secondary">{money(o.value)}</Typography>}
                                    <Typography variant="caption" color="text.secondary">{KIND[o.kind]}</Typography>
                                    <Tooltip title={explain(o)}>
                                        <InfoOutlined tabIndex={0} aria-label={explain(o)}
                                                      sx={{fontSize: 14, color: 'text.secondary', cursor: 'help'}}/>
                                    </Tooltip>
                                    {o.manual && (
                                        <>
                                            <Tag size="small" solid={false} color="warning" label="Set by hand"/>
                                            {canEdit && (
                                                <Link component="button" type="button" variant="caption"
                                                      onClick={() => setValue(o, standardOf(o), false)}>
                                                    {`Reset to ${money(standardOf(o))}`}
                                                </Link>
                                            )}
                                        </>
                                    )}
                                </Stack>
                            )}
                        </Box>
                        <Tag size="small" solid={false} color="default" label={quadrant.get(o.key)} sx={{flexShrink: 0}}/>
                    </Stack>
                ))}
                <Typography variant="caption" color="text.secondary">
                    Values start as a standard share of the category&apos;s spend (should cost 7%, standardisation 5%, ...);
                    click a value to set your own. Moving an opportunity to another group moves
                    its bar on the roadmap; in Kill the bar is hidden.
                </Typography>
            </Stack>
        </Stack>
    );
};
